import React, { useEffect, useState } from 'react';
import { colors } from '../../styles/tokens';
import type { NegadoProductoRow, NegadoTallaRow } from '../../types/indicadores.types';
import { fmtInt } from '../../utils/format';
import { useEsMovil } from '../../hooks/useEsMovil';
import { estatusBadgeColors } from '../../utils/negados';
import { useTableSort, type SortDir } from '../../utils/sort';
import ExportButton from '../ExportButton';
import ModalEnfoque from '../ModalEnfoque';
import {
  exportXlsx,
  buildNegadosProductosSheet,
  buildNegadosTallasSheet,
} from '../../utils/exportXlsx';

export interface NegadosCardsProps {
  /** `GET /dashboard/negados-productos` (top 40, ordenado desc por negados). */
  productos: NegadoProductoRow[];
  /** `GET /dashboard/negados-tallas` (top 40, ordenado desc por negados). */
  tallas: NegadoTallaRow[];
  /**
   * Clic en una fila de "Productos más negados" (PT7, drill "Detalle
   * Negado"): dispara con la fila completa. Opcional para no romper a
   * consumidores/pruebas que no les importa el drill. El clic en un `th`
   * de encabezado sigue ordenando y NO dispara este callback (son elementos
   * distintos: `th` vive en el `thead`, la fila clicable en el `tbody`).
   */
  onSelectProducto?: (row: NegadoProductoRow) => void;
  /**
   * Qué acota y qué NO acota a estas tablas (12-oct): los dos endpoints sólo
   * aceptan UNA cadena y UNA tienda, así que con Regional, Supervisor o varias
   * tiendas elegidas siguen siendo de todo el ámbito. Antes nada lo decía, y el
   * top 40 se leía al lado del total del embudo como si fueran lo mismo (S40:
   * 26.991 negados en el embudo contra 1.483 en el top 40 de productos).
   */
  alcance?: string;
}

const thBase: React.CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
  fontWeight: 700,
};

const theadRowStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: '#fff',
  boxShadow: 'inset 0 -1px 0 #e7e9ef',
};

const cardStyle: React.CSSProperties = {
  background: colors.card,
  border: `1px solid ${colors.border}`,
  borderRadius: 16,
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(20,28,54,.05)',
};

/**
 * Barra de título de cada tarjeta. En móvil baja el relleno lateral a 14 px y
 * se permite envolver: el título más "Expandir" más "Excel" no caben en una
 * fila de 390 px, y sin `wrap` los botones se salían de la pantalla.
 */
function estiloTituloTarjeta(esMovil: boolean): React.CSSProperties {
  return {
    background: colors.brand,
    color: '#fff',
    padding: esMovil ? '12px 14px' : '12px 20px',
    fontWeight: 800,
    fontSize: 14,
    letterSpacing: '.02em',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    ...(esMovil ? { flexWrap: 'wrap' as const } : null),
  };
}

/** Línea de alcance bajo el título de la barra azul (qué acota y qué no). */
const estiloAlcance: React.CSSProperties = {
  display: 'block',
  marginTop: 2,
  fontSize: 11.5,
  fontWeight: 500,
  letterSpacing: 0,
  opacity: 0.85,
};

/**
 * Botón "expandir/contraer" de las tarjetas de negados (ítem 5, 2026-07-28).
 * En móvil pasa de 28 px de alto a 44, el mínimo que un dedo acierta.
 */
function estiloExpandirBtn(esMovil: boolean): React.CSSProperties {
  return {
    background: 'rgba(255,255,255,.14)',
    border: 'none',
    borderRadius: 8,
    color: '#fff',
    fontSize: esMovil ? 12.5 : 11.5,
    fontWeight: 700,
    padding: esMovil ? '10px 14px' : '6px 11px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    ...(esMovil ? { minHeight: 44 } : null),
  };
}

/** Placeholder de foto — línea 134 del handoff. Se usa como fallback cuando no hay `imageUrl` o la foto real no carga (404). */
const PhotoPlaceholder: React.FC = () => (
  <div
    style={{
      width: 46,
      height: 32,
      borderRadius: 8,
      background: colors.photoPlaceholderBg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <svg width="34" height="22" viewBox="0 0 48 30" fill={colors.photoPlaceholderIcon}>
      <path d="M3 20c0-3 3-4 7-4h7c3-3 8-4 13-1 4 2 8 3 13 4 2 .3 2 3-.5 3H4c-.7 0-1-.6-1-1.2z" />
    </svg>
  </div>
);

export interface ProductPhotoProps {
  /** `NegadoProductoRow.imageUrl` (CloudFront); puede no existir de verdad (muchos productos 404). */
  imageUrl: string;
  alt: string;
}

/**
 * Foto real del producto en "Productos más negados" (antes un placeholder
 * fijo — ahora el backend expone `imageUrl` por fila). Si `imageUrl` viene
 * vacío, o la carga falla (`onError`, el caso común: catálogo de fotos
 * incompleto), cae al mismo `PhotoPlaceholder` de siempre en vez de mostrar
 * un ícono roto. `useEffect` resetea el estado de error si `imageUrl` cambia
 * (nueva página/orden de datos), para no quedar pegado al fallback de una
 * fila anterior que reutilice el mismo componente.
 *
 * Exportado (PT7) para que `DetalleNegadoModal` la reuse tal cual en su
 * header — NO duplicar este componente.
 */
export const ProductPhoto: React.FC<ProductPhotoProps> = ({ imageUrl, alt }) => {
  const [failed, setFailed] = useState(!imageUrl);

  useEffect(() => {
    setFailed(!imageUrl);
  }, [imageUrl]);

  if (failed) {
    return <PhotoPlaceholder />;
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      onError={() => setFailed(true)}
      style={{
        width: 46,
        height: 32,
        borderRadius: 8,
        objectFit: 'contain',
        background: colors.photoPlaceholderBg,
      }}
    />
  );
};

/** Columna ordenable de un encabezado (clic -> `toggle(key)` del `useTableSort` de esa tabla). */
interface ColumnaOrdenable<T> {
  key: keyof T;
  label: string;
  style: React.CSSProperties;
  testId: string;
}

/**
 * Encabezado `<th>` clicable + indicador ▲/▼, compartido por las 2 tablas de
 * este archivo (evita duplicar la misma interacción de sort dos veces).
 * Accesible: `role="button"` + `tabIndex` + Enter/Espacio disparan el mismo
 * `toggle` que el clic.
 */
function renderSortableTh<T>(
  col: ColumnaOrdenable<T>,
  sort: { key: keyof T; dir: SortDir },
  toggle: (key: keyof T) => void,
): React.ReactElement {
  return (
    <th
      key={String(col.key)}
      data-testid={col.testId}
      role="button"
      tabIndex={0}
      onClick={() => toggle(col.key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(col.key);
        }
      }}
      style={{ ...col.style, cursor: 'pointer', userSelect: 'none' }}
    >
      {col.label}
      {sort.key === col.key && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
    </th>
  );
}

/**
 * El equivalente MÓVIL del encabezado ordenable: un chip tocable de 44 px.
 *
 * En teléfono la tabla desaparece (se pinta una tarjeta por fila, ver más
 * abajo), y con ella se irían los `th` que ordenan. Estos chips llevan **el
 * mismo `data-testid` y llaman al mismo `toggle`** que su `th` de escritorio,
 * así que el control de orden no se pierde y nada que dependa de esos
 * identificadores se rompe. Nunca coexisten: o se pinta la tabla, o la lista.
 */
function renderSortChipMovil<T>(
  col: ColumnaOrdenable<T>,
  sort: { key: keyof T; dir: SortDir },
  toggle: (key: keyof T) => void,
): React.ReactElement {
  const activo = sort.key === col.key;
  return (
    <button
      key={String(col.key)}
      type="button"
      data-testid={col.testId}
      onClick={() => toggle(col.key)}
      style={{
        minHeight: 44,
        padding: '10px 13px',
        borderRadius: 999,
        border: `1px solid ${activo ? colors.brandSoftBorder : colors.border}`,
        background: activo ? colors.brandSoftBg : colors.card,
        color: activo ? colors.brand : colors.texto.secondary,
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {col.label}
      {activo && (sort.dir === 'asc' ? ' ▲' : ' ▼')}
    </button>
  );
}

const COLUMNAS_PRODUCTOS: ColumnaOrdenable<NegadoProductoRow>[] = [
  { key: 'descripcion', label: 'Descripción', style: { ...thBase, textAlign: 'left', padding: '10px 16px' }, testId: 'th-producto-descripcion' },
  { key: 'status', label: 'Estatus', style: { ...thBase, textAlign: 'left', padding: '10px 10px' }, testId: 'th-producto-status' },
  { key: 'negados', label: 'Negados', style: { ...thBase, textAlign: 'right', padding: '10px 12px' }, testId: 'th-producto-negados' },
];

const COLUMNAS_TALLAS: ColumnaOrdenable<NegadoTallaRow>[] = [
  { key: 'sucursal', label: 'Sucursal', style: { ...thBase, textAlign: 'left', padding: '10px 16px' }, testId: 'th-talla-sucursal' },
  { key: 'descripcion', label: 'Descripción', style: { ...thBase, textAlign: 'left', padding: '10px 10px' }, testId: 'th-talla-descripcion' },
  { key: 'talla', label: 'Talla', style: { ...thBase, textAlign: 'right', padding: '10px 12px' }, testId: 'th-talla-talla' },
  { key: 'negados', label: 'Negados', style: { ...thBase, textAlign: 'right', padding: '10px 16px' }, testId: 'th-talla-negados' },
];

/** Par etiqueta/valor de las tarjetas móviles (11 px la etiqueta, 14 px la cifra: los mínimos legibles). */
const ParMovil: React.FC<{ etiqueta: string; valor: React.ReactNode; color?: string }> = ({
  etiqueta,
  valor,
  color,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '.05em',
        textTransform: 'uppercase',
        color: colors.texto.quaternary,
      }}
    >
      {etiqueta}
    </span>
    <span
      style={{
        fontSize: 14,
        fontWeight: 800,
        color: color ?? colors.texto.strong,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {valor}
    </span>
  </div>
);

/** Contenedor de los chips de orden en móvil (una sola fila envolvente, bajo el título). */
const barraOrdenMovil: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  padding: '10px 12px',
  background: colors.footerBg,
  borderBottom: `1px solid ${colors.rowBorder}`,
};

const etiquetaOrdenMovil: React.CSSProperties = {
  width: '100%',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.05em',
  textTransform: 'uppercase',
  color: colors.texto.quaternary,
};

/**
 * Las 2 tarjetas de negados de la vista Global: "Productos más negados"
 * (descripción + ICOD, insignia de estatus por color, negados, placeholder
 * de foto) y "Detalle de negados por talla" (sucursal, descripción, talla,
 * negados). Recreadas pixel-exact de `Tablero Colectivo Moda.dc.html`
 * (líneas 122-159); el color de la insignia sale de `estatusBadgeColors`
 * (`utils/negados.ts`), que mapea los 9 estatus reales de Proscai.
 *
 * EN TELÉFONO ambas tablas dejan de ser tablas: 4 columnas no caben en 390 px
 * y arrastrar de lado para leer una cifra es la salida perezosa. Cada fila se
 * pinta como una tarjeta —nombre primero, luego los pares etiqueta/valor que
 * de verdad se miran— y el orden se controla con chips que llaman al mismo
 * `toggle` que los encabezados. Los datos, el orden y el export son los
 * mismos: sólo cambia la disposición.
 */
const NegadosCards: React.FC<NegadosCardsProps> = ({ productos, tallas, onSelectProducto, alcance }) => {
  const esMovil = useEsMovil();
  // Default = "mis más negados hasta arriba" (feedback del usuario): ambas
  // tablas ordenan desc por `negados` al montar, igual que el orden con el
  // que ya llegan de `GET /dashboard/negados-productos`/`negados-tallas`.
  // El clic en un encabezado reordena SOLO estas filas ya cargadas (top 40
  // acotado por negados desde el backend) — no vuelve a pedir datos.
  const { sorted: productosOrdenados, sort: sortProductos, toggle: toggleProductos } = useTableSort(
    productos,
    'negados',
    'desc',
  );
  const { sorted: tallasOrdenadas, sort: sortTallas, toggle: toggleTallas } = useTableSort(tallas, 'negados', 'desc');
  const [hoveredProductoKey, setHoveredProductoKey] = useState<string | null>(null);
  // 2026-08-24: "Expandir" ya no estira la tarjeta dentro de la página —eso es
  // lo que hacía perder el hilo, porque empujaba todo lo de abajo— sino que
  // abre el MODO ENFOQUE, el equivalente al Expandir de Power BI: la misma
  // tabla a pantalla casi completa, con su export, y al cerrar todo vuelve a su
  // sitio. Ver `ModalEnfoque`.
  // OJO: ambos endpoints ya vienen recortados al top 40 server-side, así que el
  // enfoque muestra TODO lo que hay en el cliente — no pide más filas.
  const [enfoque, setEnfoque] = useState<'productos' | 'tallas' | null>(null);
  const expProductos = enfoque === 'productos';
  const expTallas = enfoque === 'tallas';

  const estiloEnfocado: React.CSSProperties = { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' };
  const tituloTarjeta = estiloTituloTarjeta(esMovil);
  const expandirBtn = estiloExpandirBtn(esMovil);

  /**
   * Alto del área desplazable. En móvil sube de 260 a 340 px porque cada fila
   * ocupa ahora dos o tres líneas; `overflowX: hidden` garantiza que la
   * tarjeta no arrastre de lado aunque una descripción sea muy larga.
   */
  const estiloScroll = (expandida: boolean): React.CSSProperties => ({
    maxHeight: expandida ? undefined : esMovil ? 340 : 260,
    flex: expandida ? 1 : undefined,
    minHeight: 0,
    overflow: 'auto',
    ...(esMovil ? { overflowX: 'hidden' as const } : null),
  });

  // En enfoque, el export sube a la barra del modal (una sola cabecera).
  const exportProductos = (
    <ExportButton
      testid="negados-productos"
      variant="dark"
      disabled={productos.length === 0}
      onExport={() => exportXlsx([buildNegadosProductosSheet(productos)], 'negados-productos.xlsx')}
    />
  );
  const exportTallas = (
    <ExportButton
      testid="negados-tallas"
      variant="dark"
      disabled={tallas.length === 0}
      onExport={() => exportXlsx([buildNegadosTallasSheet(tallas)], 'negados-tallas.xlsx')}
    />
  );

  const tarjetaProductos = (
    <div data-testid="negados-productos" style={{ ...cardStyle, ...(expProductos ? estiloEnfocado : {}) }}>
      {!expProductos && (
        <div style={tituloTarjeta}>
          <span>
            Top 40 · Productos más negados
            {alcance && (
              <span data-testid="negados-productos-alcance" style={estiloAlcance}>
                {alcance}
              </span>
            )}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              data-testid="negados-productos-expandir"
              onClick={() => setEnfoque('productos')}
              style={expandirBtn}
            >
              Expandir
            </button>
            {exportProductos}
          </span>
        </div>
      )}
      {esMovil && (
        <div style={barraOrdenMovil} role="group" aria-label="Ordenar productos más negados">
          <span style={etiquetaOrdenMovil}>Ordenar por</span>
          {COLUMNAS_PRODUCTOS.map((col) => renderSortChipMovil(col, sortProductos, toggleProductos))}
        </div>
      )}
      <div style={estiloScroll(expProductos)}>
        {esMovil ? (
          productosOrdenados.map((p, i) => {
            const { bg, text } = estatusBadgeColors(p.status);
            const rowKey = `${p.productId}|${p.status}|${i}`;
            return (
              <div
                key={rowKey}
                data-testid={`fila-producto-${rowKey}`}
                onClick={() => onSelectProducto?.(p)}
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  padding: '12px 14px',
                  borderBottom: `1px solid ${colors.rowBorder}`,
                  cursor: 'pointer',
                }}
              >
                <ProductPhoto imageUrl={p.imageUrl} alt={p.descripcion} />
                {/* `minWidth: 0` o el flex se niega a encoger este bloque y la
                    descripción larga empuja la tarjeta fuera de la pantalla. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 13.5,
                      color: colors.texto.strong,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {p.descripcion}
                  </div>
                  <div style={{ fontSize: 11, color: colors.texto.quaternary, fontWeight: 600, marginTop: 2 }}>
                    ICOD {p.icod}
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'space-between',
                      gap: 10,
                      marginTop: 8,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        fontSize: 11,
                        fontWeight: 700,
                        padding: '4px 9px',
                        borderRadius: 6,
                        background: bg,
                        color: text,
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {p.status}
                    </span>
                    <ParMovil etiqueta="Negados" valor={fmtInt(p.negados)} color={colors.rojo} />
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={theadRowStyle}>
                {COLUMNAS_PRODUCTOS.map((col) => renderSortableTh(col, sortProductos, toggleProductos))}
                <th style={{ padding: '10px 16px' }} />
              </tr>
            </thead>
            <tbody>
              {productosOrdenados.map((p, i) => {
                const { bg, text } = estatusBadgeColors(p.status);
                const rowKey = `${p.productId}|${p.status}|${i}`;
                const hovered = hoveredProductoKey === rowKey;
                return (
                  <tr
                    key={rowKey}
                    data-testid={`fila-producto-${rowKey}`}
                    onClick={() => onSelectProducto?.(p)}
                    onMouseEnter={() => setHoveredProductoKey(rowKey)}
                    onMouseLeave={() => setHoveredProductoKey(null)}
                    style={{
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.rowBorder}`,
                      background: hovered ? colors.rowHover : 'transparent',
                    }}
                  >
                    <td style={{ padding: '11px 16px' }}>
                      <div style={{ fontWeight: 700, color: colors.texto.strong }}>{p.descripcion}</div>
                      <div style={{ fontSize: 11, color: colors.texto.quaternary, fontWeight: 600, marginTop: 1 }}>
                        ICOD {p.icod}
                      </div>
                    </td>
                    <td style={{ padding: '11px 10px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          fontSize: 10.5,
                          fontWeight: 700,
                          padding: '3px 8px',
                          borderRadius: 6,
                          background: bg,
                          color: text,
                        }}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: '11px 12px',
                        textAlign: 'right',
                        fontWeight: 800,
                        color: colors.rojo,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {fmtInt(p.negados)}
                    </td>
                    <td style={{ padding: '8px 16px' }}>
                      <ProductPhoto imageUrl={p.imageUrl} alt={p.descripcion} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  const tarjetaTallas = (
    <div data-testid="negados-tallas" style={{ ...cardStyle, ...(expTallas ? estiloEnfocado : {}) }}>
      {!expTallas && (
        <div style={tituloTarjeta}>
          <span>
            Top 40 · Detalle de negados por talla
            {alcance && (
              <span data-testid="negados-tallas-alcance" style={estiloAlcance}>
                {alcance}
              </span>
            )}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              data-testid="negados-tallas-expandir"
              onClick={() => setEnfoque('tallas')}
              style={expandirBtn}
            >
              Expandir
            </button>
            {exportTallas}
          </span>
        </div>
      )}
      {esMovil && (
        <div style={barraOrdenMovil} role="group" aria-label="Ordenar negados por talla">
          <span style={etiquetaOrdenMovil}>Ordenar por</span>
          {COLUMNAS_TALLAS.map((col) => renderSortChipMovil(col, sortTallas, toggleTallas))}
        </div>
      )}
      <div style={estiloScroll(expTallas)}>
        {esMovil ? (
          tallasOrdenadas.map((d, i) => (
            <div
              key={`${d.sucursal ?? ''}-${d.descripcion}-${d.talla}-${i}`}
              style={{ padding: '12px 14px', borderBottom: `1px solid ${colors.rowBorder}` }}
            >
              {/* Lo primero, el nombre: la sucursal es lo que se busca al mirar
                  esta tabla en el teléfono; las cifras van debajo. */}
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13.5,
                  color: colors.texto.strong,
                  overflowWrap: 'anywhere',
                }}
              >
                {d.sucursal ?? '—'}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: colors.texto.body,
                  marginTop: 2,
                  overflowWrap: 'anywhere',
                }}
              >
                {d.descripcion}
              </div>
              <div style={{ display: 'flex', gap: 22, marginTop: 8, flexWrap: 'wrap' }}>
                <ParMovil etiqueta="Talla" valor={d.talla} color={colors.texto.secondary} />
                <ParMovil etiqueta="Negados" valor={fmtInt(d.negados)} color={colors.rojo} />
              </div>
            </div>
          ))
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr style={theadRowStyle}>
                {COLUMNAS_TALLAS.map((col) => renderSortableTh(col, sortTallas, toggleTallas))}
              </tr>
            </thead>
            <tbody>
              {tallasOrdenadas.map((d, i) => (
                <tr
                  key={`${d.sucursal ?? ''}-${d.descripcion}-${d.talla}-${i}`}
                  style={{ borderBottom: `1px solid ${colors.rowBorder}` }}
                >
                  <td style={{ padding: '11px 16px', fontWeight: 700, color: colors.texto.strong, whiteSpace: 'nowrap' }}>
                    {d.sucursal ?? '—'}
                  </td>
                  <td style={{ padding: '11px 10px', color: colors.texto.body }}>{d.descripcion}</td>
                  <td style={{ padding: '11px 12px', textAlign: 'right', color: colors.texto.secondary, fontWeight: 600 }}>
                    {d.talla}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'right', fontWeight: 700, color: colors.rojo }}>
                    {fmtInt(d.negados)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );

  if (enfoque) {
    const esProductos = enfoque === 'productos';
    return (
      <ModalEnfoque
        testid={esProductos ? 'negados-productos' : 'negados-tallas'}
        titulo={esProductos ? 'Top 40 · Productos más negados' : 'Top 40 · Negados por talla'}
        subtitulo={
          esProductos
            ? `${productos.length} producto${productos.length === 1 ? '' : 's'}`
            : `${tallas.length} registro${tallas.length === 1 ? '' : 's'}`
        }
        acciones={esProductos ? exportProductos : exportTallas}
        onClose={() => setEnfoque(null)}
      >
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {esProductos ? tarjetaProductos : tarjetaTallas}
        </div>
      </ModalEnfoque>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        // En el teléfono, UNA columna. `minmax(380px, 1fr)` es un ancho MÍNIMO
        // FIJO: en una pantalla de 390 px la pista seguía midiendo 380 px más
        // el relleno de la página, y la rejilla entera se salía de lado.
        gridTemplateColumns: esMovil ? '1fr' : 'repeat(auto-fit, minmax(380px, 1fr))',
        gap: esMovil ? 12 : 14,
        marginTop: esMovil ? 12 : 14,
      }}
    >
      {tarjetaProductos}
      {tarjetaTallas}
    </div>
  );
};

export default NegadosCards;
